<template>
  <div class="vuse-content-wrapper">
    <vuse-section-definition
      :title="pageTitle"
      icon="attach_money"
      v-show="!hideTitle"
    ></vuse-section-definition>
    <v-container fluid> 
      <v-card :loading="isLoading && !loaded">        
        <v-form ref="form" v-model="valid" @submit.prevent="submit()">
          <v-card-title>
            <v-row>
              <v-col>
                <h3>Bid #{{ quoteNumber }}</h3>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-overlay :value="isLoading && !loaded" :absolute="true" :opacity="0.01"></v-overlay>
            <AlertMessage />
            <v-row>
              <v-col cols="12" lg="4" md="6">
                <date-picker
                  v-model="form.dateExpired"
                  label="Due Date"
                  :error-messages="[].concat(fieldErrors('form.dateExpired'))"
                  :blur="$v.form.dateExpired.$touch()"
                />
              </v-col>
              <v-col cols="12" lg="4" md="6">
                <v-text-field
                  v-model="form.referenceNumber"
                  label="Reference Number"
                  :error-messages="
                    [].concat(fieldErrors('form.referenceNumber'))
                  "
                  @blur="$v.form.referenceNumber.$touch()"
                />
              </v-col>
              <v-col cols="12" lg="4" md="6">
                <client-select
                  v-model="form.clientId"
                  label="Client"
                  :error-messages="[].concat(fieldErrors('form.clientId'))"
                  @blur="$v.form.clientId.$touch()"
                />
              </v-col>
              <v-col cols="12" lg="4" md="6">
                <date-picker
                  v-model="form.date"
                  label="Date"
                  :error-messages="[].concat(fieldErrors('form.date'))"
                  :blur="$v.form.date.$touch()"
                />
              </v-col>
            </v-row>
            <v-card
              v-for="(item, itemIdx) in lineItems"
              :key="`d_${itemIdx}`"
              class="mt-5"
            >
              <v-card-text>
                <v-row>
                  <v-col cols="12" lg="10" md="10" sm="6">
                    Item
                    <div>
                      <quote-item-select
                        :return-object="true"
                        label="Item"
                        v-model="lineItems[itemIdx].itemSrc"
                        @change="onItemChange($event, itemIdx)"
                        :filter-type="lineItems[itemIdx].type"
                        :hide-details="true"
                        :solo="true"
                      />
                    </div>
                  </v-col>
                  <v-col cols="12" lg="2" md="2" sm="6">
                    Type
                    <div>
                      <TypeSelect
                        label="Type"
                        v-model="lineItems[itemIdx].type"
                        :disabled="!!lineItems[itemIdx].itemSrc"
                        hide-details
                        dense
                        solo
                      />
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" lg="12" md="12" sm="12">
                    Description
                    <v-textarea
                      label="Description"
                      v-model="lineItems[itemIdx].description"
                      hide-details
                      auto-grow
                      dense
                      solo
                      rows="2"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" lg="2" md="2" sm="4">
                    <div>Qty</div>
                    <v-text-field
                      label="Quantity"
                      type="number"
                      v-model="lineItems[itemIdx].quantity"
                      hide-details
                      dense
                      solo
                    />
                  </v-col>
                  <v-col cols="12" lg="2" md="2" sm="4">
                    <div>Cost</div>
                    <v-text-field
                      label="Cost"
                      type="number"
                      v-model="lineItems[itemIdx].cost"
                      hide-details
                      dense
                      solo
                    />
                  </v-col>
                  <v-col cols="12" lg="2" md="2" sm="4">
                    <div>Price</div>
                    <v-text-field
                      label="Price"
                      type="number"
                      v-model="lineItems[itemIdx].price"
                      hide-details
                      dense
                      solo
                    />
                  </v-col>

                  <v-col cols="6" lg="4" md="4" sm="12">
                    <div>Date</div>
                    <date-picker
                      label="Date"
                      v-model="lineItems[itemIdx].date"
                      hide-details
                      dense
                      solo
                    />
                  </v-col>

                  <v-col cols="6" lg="4" md="4" sm="12">
                    <v-row>
                      <v-col cols="6" lg="6" md="4" sm="2">
                        <div>Tax</div>
                        <v-checkbox
                          class="mt-0"
                          v-model="lineItems[itemIdx].taxable"
                          :hide-details="true"
                          :label="`${taxRate}%`"
                        />
                      </v-col>
                      <v-col cols="6" lg="6" md="4" sm="2">
                        <div>Total</div>
                        <div style="padding: 5px 0px">
                          {{
                            subTotalPriceGrand(lineItems[itemIdx]) | formatPrice
                          }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  @click="removeItem(itemIdx)"
                  rounded
                  fab
                  color="error"
                  x-small
                  ><v-icon>delete_forever</v-icon></v-btn
                >
              </v-card-actions>
            </v-card>

            <v-row>
              <v-col>
                <v-btn
                  class="mt-5"
                  @click="addItem()"
                  rounded
                  fab
                  color="info"
                  x-small
                  ><v-icon>add</v-icon></v-btn
                >
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-simple-table cellpadding="3" width="100%">
                  <tbody>
                    <tr>
                      <th width="20%">Total Parts</th>
                      <td width="30%">{{ formatPrice(totalPricePart) }}</td>
                      <th width="20%">Total Labor</th>
                      <td width="30%">{{ formatPrice(totalPriceLabor) }}</td>
                    </tr>
                    <tr>
                      <th>Net Parts</th>
                      <td>{{ formatPrice(totalNetPart) }}</td>
                      <th>Net Labor</th>
                      <td>{{ formatPrice(totalNetLabor) }}</td>
                    </tr>
                    <tr>
                      <th colspan="3" class="text-right">
                        <h2>Gross Total:</h2>
                      </th>
                      <td>
                        <h2>{{ (totalTax + totalGross) | formatPrice }}</h2>
                      </td>
                    </tr>
                    <tr>
                      <th colspan="3" class="text-right">
                        <h2>Tax Total:</h2>
                      </th>
                      <td>
                        <h2>{{ totalTax | formatPrice }}</h2>
                      </td>
                    </tr>
                    <tr>
                      <th colspan="3" class="text-right">
                        <h2>Net Total:</h2>
                      </th>
                      <td>
                        <h2>{{ formatPrice(totalNet) }}</h2>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col> </v-col>
              <v-col class="text-right">
                <v-btn class="mr-3" @click="cancel()">Cancel</v-btn>
                <v-btn
                  type="submit"
                  color="primary mr-3"
                  :disabled="$v.$invalid"
                  :loading="loading"
                  >{{ labelButton }}</v-btn
                >
              </v-col>
            </v-row>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import ClientSelect from "components/ClientSelect";
import DatePicker from "components/DatePicker";
// import QuoteItemSelect from "components/InvoiceItemSelect";
import QuoteItemSelect from "components/ItemCombo";
import TypeSelect from "components/TypeSelect";
// import QuoteStatusSelect from "components/QuoteStatusSelect";
import { formatPrice } from "src/utils/helpers";
import AlertMessage from "src/components/AlertMessage";
import { required } from "vuelidate/lib/validators";
import validationMixin from "@/mixins/validationMixin";
import formatterMixin from "src/mixins/formatterMixin";
import { mapActions, mapState } from "vuex";

const defaultLineItem = {
  clientId: null,
  cost: 0,
  date: null,
  description: null,
  id: null,
  isInvoiced: false,
  isItemPurchase: false,
  itemId: null,
  itemType: null,
  message: null,
  price: 0,
  quantity: 1,
  status: null,
  taskId: null,
  taxRate: null,
  taxable: false,
  title: null,
  type: null,
  itemSrc: undefined,
};

export default {
  props: {
    dialog: Boolean,
    clone: Boolean,
    item: Object,
    hideTitle: Boolean,
    clientId: Number,
  },
  mixins: [formatterMixin, validationMixin],
  components: {
    AlertMessage,
    ClientSelect,
    QuoteItemSelect,
    DatePicker,
    TypeSelect,
    // QuoteStatusSelect,
  },
  data() {
    return {
      valid: false,
      loaded: false,
      loading: false,
      dateRange: {},
      form: {
        dateExpired: new Date().toISOString().substr(0, 10),
        referenceNumber: "",
        clientId: "",
        date: new Date().toISOString().substr(0, 10),
        status: "saved",
      },
      lineItems: [],
    };
  },

  validations: {
    form: {
      dateExpired: { },
      referenceNumber: {},
      clientId: { required },
      date: { },
      status: {  },
    },
    lineItems: {
      required,
    },
  },
  validationMessages: {
    form: {
      dateExpired: {
        required: "Required",
      },
      status: {
        required: "Required",
      },
      referenceNumber: {},
      clientId: { required: "Required" },
      date: { required: "Required" },
    },
  },

  watch: {
    lineItems(items) {
      Object.entries(items).map((value) => {
        let itemIdx = value[0];
        let itemVal = value[1];
        this.lineItems[itemIdx].total = this.subTotalPrice(itemVal);
      });
    },

    async dialog(n) {
      if (n) {
        await this.reload();
      } else {
        this.form = {
          dateExpired: new Date().toISOString().substr(0, 10),
          referenceNumber: "",
          date: new Date().toISOString().substr(0, 10),
          clientId: this.clientId,
          status: "saved"
        };
        this.lineItems = [];
      }
    },

    async quoteId(n) {
      if (n && n.id) {
        await this.reload();
      }
    },
  },

  computed: {
    ...mapState({
      quote: (state) => state.quote,
    }),

    isLoading() {
      return this.$store.getters['client/getFetchIsLoading'] || this.loading;
    },

    labelButton() {
      if (this.isClone) {
        return "Clone";
      } else if (this.isEdit) {
        return "Save";
      } else {
        return "Add";
      }
    },

    pageTitle() {
      if (this.isClone) {
        return "Clone Bid";
      } else if (this.isEdit) {
        return "Edit Bid";
      } else {
        return "New Bid";
      }
    },

    isEdit() {
      return this.quoteId ? true : false;
    },

    isClone() {
      return this.clone ? true : false;
    },

    isNew() {
      return !this.isEdit && !this.isClone;
    },

    quoteNumber() {
      if (this.isClone) {
        return this.quote.nextNumber;
      } else if (this.isEdit) {
        return this.existingQuote.id;
      } else {
        return this.quote.nextNumber;
      }
    },

    existingQuote() {
      if (this.isEdit) {
        return this.quote.single;
      } else if (this.isClone) {
        return this.quote.single;
      } else {
        return {};
      }
    },

    stateQuote() {
      return this.quote.single;
    },

    /**
     * Get existing quoteId
     */
    quoteId() {
      let { quoteId } = this.$route.params;
      if (!quoteId) {
        quoteId = this.optional(this.item, "id", "");
      }
      return quoteId;
    },

    totalTax() {
      let total = 0;
      if (typeof this.lineItems === "undefined") {
        return total;
      }
      this.lineItems.map((item) => {
        total += this.calculateTax(item);
      });
      return total;
    },

    totalGross() {
      let total = 0;
      this.lineItems.map((item) => {
        total += this.subTotalPrice(item);
      });
      return total;
    },

    totalCost() {
      let total = 0;
      this.lineItems.map((item) => {
        total += this.subTotalCost(item);
      });
      return total;
    },

    totalNet() {
      return this.totalGross - this.totalCost;
    },

    totalPricePart() {
      let total = 0;
      this.lineItems
        .filter((item) => item.type == "Parts")
        .map((item) => {
          total += this.subTotalPrice(item);
        });
      return total;
    },

    totalCostPart() {
      let total = 0;
      this.lineItems
        .filter((item) => item.type == "Parts")
        .map((item) => {
          total += this.subTotalCost(item);
        });
      return total;
    },

    totalNetPart() {
      return this.totalPricePart - this.totalCostPart;
    },

    totalPriceLabor() {
      let total = 0;
      this.lineItems
        .filter((item) => item.type == "Labor")
        .map((item) => {
          total += this.subTotalPrice(item);
        });
      return total;
    },

    totalCostLabor() {
      let total = 0;
      this.lineItems
        .filter((item) => item.type == "Labor")
        .map((item) => {
          total += this.subTotalCost(item);
        });
      return total;
    },

    totalNetLabor() {
      return this.totalPriceLabor - this.totalCostLabor;
    },

    taxRate() {
      return this.$store.getters["account/getTaxRate"];
    },
  },

  async created() {
    if (this.clientId) {
      this.form.clientId = this.clientId;
    }
    await this.reload();
    await this.accountGetTaxRate();
    this.loaded = true;
  },

  beforeDestroy() {
    this.loaded = false;
  },

  methods: {
    ...mapActions("quote", ["create", "update", "getNextNumber", "getById"]),
    ...mapActions({
      accountGetTaxRate: "account/getTaxRate",
      quoteClone: "quote/clone"
    }),

    isItemFilled(item) {
      return typeof item.itemId === "number";
    },

    async reload() {
      this.loading = true;
      await this.getNextNumber();
      if (this.quoteId) {
        await this.getById(this.quoteId);
        this.form = {
          ...this.form,
          ...this.quote.single
        };

        if (this.isClone) {
          // when state isClone, set dateExpired to null & date to now
          this.form.dateExpired = null;          
          this.form.date = this.momentLocal().format("YYYY-MM-DD");
        }

        this.lineItems = [...this.quote.single.lineItems] || [];
        this.lineItems = this.lineItems.map((item) => {
          // reload task
          // since we have taskId, itemId need refactor to clearly set this polymorph
          if (!item.itemId || typeof item.itemId === "number") {
            item.itemSrc = item.title;
          }

          return {
            ...item,
          };
        });
      }
      this.loading = false;
    },

    async submit() {
      if (!this.valid) {
        return false;
      }

      this.loading = true;

      let lineItems = this.lineItems.map((item) => {
        if (!item.id) {
          // this deleted, because backend will error if post with id=null
          delete item.id;
        }

        if (this.quoteId) {
          delete item.id;
        }

        if (!item.date) {
          delete item.date;
        }

        return {
          ...item,
          taxRate: item.taxable ? this.taxRate : 0,
        };
      });

      // delete this.lineItems;

      let quote = {
        ...this.form,
        title: "this is title",
      };

  
      delete quote.lineItems;

      if (quote.dateExpired === "") {
        // must be send in payload as null
        quote.dateExpired = null;
      }
 
      let payload = {
        quote,
        lineItems,
      };

      if (this.quoteId && !this.isClone) {
        await this.update({
          id: this.quoteId,
          ...payload,
        });
      } else {
        payload.quote.status = "saved";
        if (this.isClone) {
          delete payload.id;
          delete payload.quote.id;
          await this.quoteClone(payload);
        } else {        
          await this.create(payload);
        }
      }
      this.loading = false;

      if (!this.quote.status.error) {
        if (this.clientId) {
          this.cancel();
        } else {
          this.$router.push("/bids");
        }
      }
    },

    calculateTax(item) {
      if (item.taxable) {
        return (this.taxRate / 100) * this.subTotalPrice(item);
      }
      return 0;
    },

    subTotalPrice(item) {
      return item.quantity * item.price;
    },

    subTotalPriceGrand(item) {
      return this.calculateTax(item) + this.subTotalPrice(item);
    },

    subTotalCost(item) {
      return item.quantity * item.cost;
    },

    formatPrice(value) {
      return formatPrice(value);
    },

    removeItem(idx) {
      let confirm = false;
      if (
        this.lineItems[idx].title ||
        this.lineItems[idx].quantity ||
        this.lineItems[idx].price ||
        this.lineItems[idx].cost ||
        this.lineItems[idx].title ||
        this.lineItems[idx].description
      ) {
        confirm = true;
      }
      if (confirm && !window.confirm("Are you sure will remove item?")) {
        return false;
      }
      this.lineItems.splice(idx, 1);
    },
    addItem() {
      this.lineItems.push({
        ...defaultLineItem,
      });

      // this.lineItems.push(defaultLineItem);
    },

    cancel() {
      if (this.clientId) {
        this.$emit("onCancel");
      } else {
        this.$router.push("/bids");
      }
    },

    onItemChange(value, index) {
      if (typeof value === "object") {
        // Detected lineItem as Item/ Item Purchase (not typed manualy, but selected from autofilled)
        this.lineItems[index].title = this.optional(value, "name", "");
        this.lineItems[index].description = this.optional(
          value,
          "description",
          ""
        );
        this.lineItems[index].type = this.optional(value, "type", "");
        this.lineItems[index].cost = this.optional(value, "cost", 0);
        this.lineItems[index].price = this.optional(value, "price", 0);
        this.lineItems[index].itemId = this.optional(value, "id", "");
      } else if (typeof value === "string") {
        // Detected lineItem as custom (typed manually)
        this.lineItems[index].title = value;
      }
    },
  },
};
</script>